import { getEnemySkillById } from '../../content/enemies/enemySkills';
import type { EnemyDef } from '../../content/enemies/enemies';
import type { CombatState, PlayerState } from '../game/types';
import { rollDodge, rollEnemySkillHit } from './accuracy';
import { resolveEnemyAttack, type EnemyAttackResult } from './enemyAttack';
import { actionBlockLabel, applyStatus, rollActionBlock } from './statuses';

function nextEnemySkill(combat: CombatState) {
  const ids = combat.enemySkillIds;
  if (ids.length === 0) {
    return undefined;
  }
  return getEnemySkillById(ids[combat.enemyTurn % ids.length] as string);
}

function scaledSkillAccuracy(base: number, floorNumber: number): number {
  return Math.min(0.95, base + Math.max(0, floorNumber - 1) * 0.012);
}

function advanceEnemyTurn(result: EnemyAttackResult): EnemyAttackResult {
  return {
    ...result,
    combat: { ...result.combat, enemyTurn: result.combat.enemyTurn + 1 },
  };
}

/** Resolve one data-driven enemy skill from its repeating skill pattern. */
export function resolveEnemyTurn(
  player: PlayerState,
  combat: CombatState,
  enemy: EnemyDef,
  rng: () => number = Math.random,
): EnemyAttackResult {
  const skill = nextEnemySkill(combat);
  if (!skill || skill.effect.type === 'lethal_attack') {
    const accuracy = skill
      ? scaledSkillAccuracy(skill.accuracy, combat.floorNumber)
      : combat.enemyAccuracy;
    return advanceEnemyTurn(
      resolveEnemyAttack(
        player,
        combat,
        { accuracy, name: enemy.name, element: skill?.element },
        rng,
      ),
    );
  }

  const blocked = rollActionBlock(combat.enemyStatuses, rng);
  if (blocked) {
    return advanceEnemyTurn({
      player,
      combat: {
        ...combat,
        log: [`${enemy.name} ${actionBlockLabel(blocked)} — bỏ lượt!`, ...combat.log].slice(0, 8),
      },
      killed: false,
      dodged: false,
      missed: false,
      skipped: true,
    });
  }

  if (!rollEnemySkillHit(scaledSkillAccuracy(skill.accuracy, combat.floorNumber), rng)) {
    return advanceEnemyTurn({
      player,
      combat: {
        ...combat,
        log: [`${enemy.name} thi triển một năng lực nhưng thất bại!`, ...combat.log].slice(0, 8),
      },
      killed: false,
      dodged: false,
      missed: true,
      skipped: false,
    });
  }

  if (rollDodge(player.stats, rng)) {
    return advanceEnemyTurn({
      player,
      combat: {
        ...combat,
        log: [`Bạn né được năng lực bí ẩn của ${enemy.name}!`, ...combat.log].slice(0, 8),
      },
      killed: false,
      dodged: true,
      missed: false,
      skipped: false,
    });
  }

  return advanceEnemyTurn({
    player,
    combat: {
      ...combat,
      playerStatuses: applyStatus(combat.playerStatuses, {
        id: skill.effect.statusId,
        remainingTurns: skill.effect.turns,
        potency: skill.effect.potency,
      }),
      log: [`${enemy.name} thi triển năng lực bí ẩn — hiệu ứng đã áp lên bạn.`, ...combat.log].slice(0, 8),
    },
    killed: false,
    dodged: false,
    missed: false,
    skipped: false,
  });
}
